import { memo, useCallback, useEffect, useState } from 'react'
import { asRunApi, type PurgePreview } from '../../lib/reportsApi.js'
import { ModalShell } from '../common/ModalShell.js'
import { AlertTriangle } from 'lucide-react'

interface PurgeReportsDialogProps {
  channelId: string
  from:      string
  to:        string
  /** Un export a-t-il été téléchargé depuis l'ouverture de la vue ? */
  exported:  boolean
  onClose:   () => void
  onPurged:  (result: { deletedAsRunLogs: number; deletedScheduleItems: number }) => void
}

/**
 * Purge des rapports : deux temps obligatoires.
 *
 * Le dialogue chiffre d'abord ce qui disparaîtra, puis exige une confirmation
 * explicite (saisie de SUPPRIMER). Les compteurs affichés sont renvoyés au
 * serveur, qui rejette la demande si la période a bougé entre-temps — on ne
 * détruit jamais autre chose que ce qui a été montré ici.
 */
export const PurgeReportsDialog = memo(function PurgeReportsDialog({
  channelId, from, to, exported, onClose, onPurged,
}: PurgeReportsDialogProps) {
  const [preview, setPreview] = useState<PurgePreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [purging, setPurging] = useState(false)
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    asRunApi.purgePreview({ channelId, from, to })
      .then(result => { if (!cancelled) setPreview(result) })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : String(err)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [channelId, from, to])

  const handlePurge = useCallback(async () => {
    if (!preview) return
    setPurging(true)
    setError(null)
    try {
      const result = await asRunApi.purge({
        channelId, from, to,
        expectedAsRunCount:        preview.asRunCount,
        expectedScheduleItemCount: preview.scheduleItemCount,
      })
      onPurged(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setPurging(false)
    }
  }, [channelId, from, preview, to, onPurged])

  const nothingToPurge = !loading && preview !== null && preview.asRunCount === 0 && preview.scheduleItemCount === 0
  const canPurge = confirmation.trim().toUpperCase() === 'SUPPRIMER' && !purging && !loading && !nothingToPurge

  return (
    <ModalShell title="Purger les rapports" size="narrow" onClose={onClose} closeDisabled={purging}>
      <div className="tp-modal__body">
        {loading && <p>Calcul du contenu de la période…</p>}

        {!loading && preview && (
          <>
            <p>
              Période du <strong>{new Date(from).toLocaleDateString('fr-FR')}</strong> au{' '}
              <strong>{new Date(to).toLocaleDateString('fr-FR')}</strong>.
            </p>
            <ul className="reports-purge__counts">
              <li><strong>{preview.asRunCount}</strong> ligne(s) d’as-run supprimée(s)</li>
              <li><strong>{preview.scheduleItemCount}</strong> item(s) de grille déjà diffusé(s) supprimé(s)</li>
              {preview.oldest && preview.newest && (
                <li>
                  De {new Date(preview.oldest).toLocaleString('fr-FR')} à {new Date(preview.newest).toLocaleString('fr-FR')}
                </li>
              )}
            </ul>

            {nothingToPurge && <p>Rien à purger sur cette période.</p>}

            {!nothingToPurge && (
              <>
                {!exported && (
                  <div className="tp-feedback tp-feedback--warning" role="alert">
                    <AlertTriangle size={14} aria-hidden="true" />
                    Aucun export n’a été téléchargé depuis l’ouverture de cette vue. Exporte le CSV
                    ou le PDF avant de purger : les as-run sont des preuves de diffusion.
                  </div>
                )}
                <p className="reports-purge__warning">
                  Cette suppression est <strong>irréversible</strong> et n’est pas couverte par un
                  historique applicatif : seule une restauration de sauvegarde PostgreSQL permettrait
                  de revenir en arrière.
                </p>
                <label className="reports-field">
                  <span className="tp-field-label">Tape SUPPRIMER pour confirmer</span>
                  <input
                    className="tp-input tp-input--data"
                    value={confirmation}
                    onChange={event => setConfirmation(event.target.value)}
                    placeholder="SUPPRIMER"
                    disabled={purging}
                    autoFocus
                  />
                </label>
              </>
            )}
          </>
        )}

        {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}
      </div>

      <footer className="tp-modal__footer">
        <button type="button" className="tp-button" onClick={onClose} disabled={purging}>Annuler</button>
        <button
          type="button"
          className="tp-button tp-button--danger"
          onClick={handlePurge}
          disabled={!canPurge}
        >
          {purging ? 'Purge…' : 'Purger définitivement'}
        </button>
      </footer>
    </ModalShell>
  )
})
